// qs-useExport.ts
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { useLayerStore } from '@/stores/qs-store-layers'
import { useToolStore } from '@/stores/qs-store-tools'
import type { KonvaStage, KonvaLayer, KonvaLine, KonvaCircle, KonvaGroup } from '@/types/qs-type-konva'
import Konva from 'konva'

interface ContentBounds {
  x: number
  y: number
  width: number
  height: number
}

interface LayerPosition {
  x: number
  y: number
}

type KonvaShape = KonvaLine | KonvaCircle | KonvaGroup

export function useExport() {
  const canvasStore = useCanvasStore()
  const layerStore = useLayerStore()
  const toolStore = useToolStore()

  const generateTimestamp = (): string => {
    const now = new Date()
    const year = now.getFullYear()
    const month = (now.getMonth() + 1).toString().padStart(2, '0')
    const day = now.getDate().toString().padStart(2, '0')
    const hours = now.getHours().toString().padStart(2, '0')
    const minutes = now.getMinutes().toString().padStart(2, '0')
    const seconds = now.getSeconds().toString().padStart(2, '0')
    return `${year}${month}${day}${hours}${minutes}${seconds}`
  }

  const generateFilename = (extension: string): string => {
    const bgColorCode = toolStore.backgroundColor.replace('#', '')
    const timestamp = generateTimestamp()
    return `vectorsketch-bg${bgColorCode}-${timestamp}.${extension}`
  }

  const downloadFile = (dataURL: string, filename: string): void => {
    const link = document.createElement('a')
    link.download = filename
    link.href = dataURL
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const isOriginalPath = (shape: KonvaShape): boolean => {
    return (shape as { attrs?: { isOriginalPath?: boolean }; name?: () => string }).attrs?.isOriginalPath === true ||
           (shape as { name?: () => string }).name?.() === 'original-path'
  }

  const getLayerPosition = (konvaLayer: KonvaLayer): LayerPosition => {
    const layerId = konvaLayer.id()?.replace('layer-', '')
    const layerData = layerId ? layerStore.layers.find(l => l.id === layerId) : null
    return layerData?.position || { x: 0, y: 0 }
  }

  const adjustPoints = (points: number[], layerPosition: LayerPosition, contentX: number, contentY: number): number[] => {
    const adjustedPoints: number[] = []
    for (let i = 0; i < points.length; i += 2) {
      adjustedPoints.push(points[i] + layerPosition.x - contentX, points[i + 1] + layerPosition.y - contentY)
    }
    return adjustedPoints
  }

  const calculateContentBounds = (padding = 100): ContentBounds => {
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    const stage = canvasStore.stage
    if (!stage) return { x: 0, y: 0, width: 800, height: 600 }

    const konvaLayers = stage.getLayers()

    konvaLayers.forEach((konvaLayer: KonvaLayer) => {
      if (konvaLayer.visible() && konvaLayer.name() !== 'background') {
        const layerPosition = getLayerPosition(konvaLayer)

        const processShape = (shape: KonvaShape): void => {
          if (isOriginalPath(shape)) return

          if (shape.className === 'Line' || shape.className === 'Path') {
            const pointsFunc = (shape as { points?: () => number[] }).points
            if (typeof pointsFunc === 'function') {
              const points = pointsFunc()
              for (let i = 0; i < points.length; i += 2) {
                const x = points[i] + layerPosition.x
                const y = points[i + 1] + layerPosition.y
                minX = Math.min(minX, x)
                minY = Math.min(minY, y)
                maxX = Math.max(maxX, x)
                maxY = Math.max(maxY, y)
              }
            }
          } else if (shape.className === 'Circle') {
            const x = (shape as { x?: () => number }).x?.() ?? 0
            const y = (shape as { y?: () => number }).y?.() ?? 0
            const radius = (shape as { radius?: () => number }).radius?.() ?? 0
            minX = Math.min(minX, x + layerPosition.x - radius)
            minY = Math.min(minY, y + layerPosition.y - radius)
            maxX = Math.max(maxX, x + layerPosition.x + radius)
            maxY = Math.max(maxY, y + layerPosition.y + radius)
          } else if (shape.className === 'Group' || (shape as { children?: KonvaShape[] }).children) {
            const children = (shape as { children?: KonvaShape[] }).children
            if (children) {
              children.forEach((child: KonvaShape) => processShape(child))
            }
          }
        }

        const children = (konvaLayer as { children?: KonvaShape[] }).children
        if (children) {
          children.forEach((child: KonvaShape) => processShape(child))
        }
      }
    })

    if (minX === Infinity) {
      return { x: 0, y: 0, width: 800, height: 600 }
    }

    return {
      x: minX - padding,
      y: minY - padding,
      width: (maxX - minX) + (padding * 2),
      height: (maxY - minY) + (padding * 2)
    }
  }

  const createLineFromShape = (shape: KonvaLine, layerPosition: LayerPosition, contentX: number, contentY: number): KonvaLine => {
    const pointsFunc = (shape as { points?: () => number[] }).points
    if (typeof pointsFunc !== 'function') {
      throw new Error('Shape has className Line/Path but no points function')
    }
    const points = pointsFunc()
    const adjustedPoints = adjustPoints(points, layerPosition, contentX, contentY)

    return new Konva.Line({
      points: adjustedPoints,
      stroke: (shape as { stroke?: () => string }).stroke?.() ?? '#000000',
      strokeWidth: (shape as { strokeWidth?: () => number }).strokeWidth?.() ?? 1,
      lineCap: (shape as { lineCap?: () => 'round' | 'butt' | 'square' }).lineCap?.() ?? 'round',
      lineJoin: (shape as { lineJoin?: () => 'round' | 'miter' | 'bevel' }).lineJoin?.() ?? 'round',
      opacity: (shape as { opacity?: () => number }).opacity?.() ?? 1,
      dash: (shape as { dash?: () => number[] }).dash?.() ?? [],
      dashOffset: (shape as { dashOffset?: () => number }).dashOffset?.() ?? 0,
      fill: (shape as { fill?: () => string }).fill?.() ?? 'transparent'
    })
  }

  const createCircleFromShape = (shape: KonvaCircle, layerPosition: LayerPosition, contentX: number, contentY: number): KonvaCircle => {
    return new Konva.Circle({
      x: ((shape as { x?: () => number }).x?.() ?? 0) + layerPosition.x - contentX,
      y: ((shape as { y?: () => number }).y?.() ?? 0) + layerPosition.y - contentY,
      radius: (shape as { radius?: () => number }).radius?.() ?? 0,
      fill: (shape as { fill?: () => string }).fill?.() ?? '#000000',
      opacity: (shape as { opacity?: () => number }).opacity?.() ?? 1
    })
  }

  const processGroupForPNG = (group: KonvaGroup, layerPosition: LayerPosition, contentX: number, contentY: number): KonvaGroup => {
    const tempGroup = new Konva.Group()
    const children = (group as { children?: KonvaShape[] }).children

    if (!children || children.length === 0) return tempGroup

    children.forEach((grandChild: KonvaShape) => {
      if (isOriginalPath(grandChild)) return

      if (grandChild.className === 'Line' || grandChild.className === 'Path') {
        try {
          const tempLine = createLineFromShape(grandChild as KonvaLine, layerPosition, contentX, contentY)
          tempGroup.add(tempLine)
        } catch (error) {
          console.warn('Failed to create line from shape:', error)
        }
      } else if (grandChild.className === 'Group') {
        const nestedGroup = processGroupForPNG(grandChild as KonvaGroup, layerPosition, contentX, contentY)
        if (nestedGroup.children.length > 0) {
          tempGroup.add(nestedGroup)
        }
      } else if (grandChild.className === 'Circle') {
        const tempCircle = createCircleFromShape(grandChild as KonvaCircle, layerPosition, contentX, contentY)
        tempGroup.add(tempCircle)
      }
    })

    return tempGroup
  }

  const exportCanvasAsPNG = (): void => {
    try {
      if (!canvasStore.stage) {
        console.error('Canvas stage not available for export')
        return
      }

      const stage = canvasStore.stage
      const contentBounds = calculateContentBounds(100)
      const { x: contentX, y: contentY, width: contentWidth, height: contentHeight } = contentBounds

      const tempContainer = document.createElement('div')
      tempContainer.style.position = 'absolute'
      tempContainer.style.top = '-9999px'
      tempContainer.style.left = '-9999px'
      document.body.appendChild(tempContainer)

      const tempStage: KonvaStage = new Konva.Stage({
        container: tempContainer,
        width: contentWidth,
        height: contentHeight
      })

      const exportLayer = new Konva.Layer()
      const konvaLayers = stage.getLayers()

      konvaLayers.forEach((konvaLayer: KonvaLayer) => {
        if (konvaLayer.visible() && konvaLayer.name() !== 'background') {
          const layerPosition = getLayerPosition(konvaLayer)
          const children = (konvaLayer as { children?: KonvaShape[] }).children

          if (children) {
            children.forEach((child: KonvaShape) => {
              if (isOriginalPath(child)) return

              if (child.className === 'Line' || child.className === 'Path') {
                try {
                  const tempLine = createLineFromShape(child as KonvaLine, layerPosition, contentX, contentY)
                  exportLayer.add(tempLine)
                } catch (error) {
                  console.warn('Failed to create line from shape:', error)
                }
              } else if (child.className === 'Group' || (child as { children?: KonvaShape[] }).children) {
                const tempGroup = processGroupForPNG(child as KonvaGroup, layerPosition, contentX, contentY)
                if (tempGroup.children.length > 0) {
                  exportLayer.add(tempGroup)
                }
              } else if (child.className === 'Circle') {
                const tempCircle = createCircleFromShape(child as KonvaCircle, layerPosition, contentX, contentY)
                exportLayer.add(tempCircle)
              }
            })
          }
        }
      })

      tempStage.add(exportLayer)

      const dataURL = tempStage.toDataURL({
        pixelRatio: 2,
        mimeType: 'image/png'
      })

      const filename = generateFilename('png')
      downloadFile(dataURL, filename)

      tempStage.destroy()
      document.body.removeChild(tempContainer)
    } catch (error) {
      console.error('Failed to export PNG:', error)
    }
  }

  const pointsToSVGPath = (adjustedPoints: number[]): string => {
    if (adjustedPoints.length < 4) return ''
    let pathData = `M ${adjustedPoints[0]} ${adjustedPoints[1]}`
    for (let i = 2; i < adjustedPoints.length; i += 2) {
      pathData += ` L ${adjustedPoints[i]} ${adjustedPoints[i + 1]}`
    }
    return pathData
  }

  const lineToSVGPath = (shape: KonvaLine, layerPosition: LayerPosition, contentX: number, contentY: number): string => {
    const pointsFunc = (shape as { points?: () => number[] }).points
    if (typeof pointsFunc !== 'function') {
      console.warn('Object has className Line/Path but no points function:', shape)
      return ''
    }
    const points = pointsFunc()
    const adjustedPoints = adjustPoints(points, layerPosition, contentX, contentY)
    const pathData = pointsToSVGPath(adjustedPoints)

    if (!pathData) return ''

    const stroke = (shape as { stroke?: () => string }).stroke?.() ?? '#000000'
    const strokeWidth = (shape as { strokeWidth?: () => number }).strokeWidth?.() ?? 1
    const opacity = (shape as { opacity?: () => number }).opacity?.() ?? 1
    const lineCap = (shape as { lineCap?: () => 'round' | 'butt' | 'square' }).lineCap?.() ?? 'round'
    const lineJoin = (shape as { lineJoin?: () => 'round' | 'miter' | 'bevel' }).lineJoin?.() ?? 'round'
    const dashArray = (shape as { dash?: () => number[] }).dash?.() ?? []
    const dashOffset = (shape as { dashOffset?: () => number }).dashOffset?.() ?? 0

    let svgAttributes = `d="${pathData}" stroke="${stroke}" stroke-width="${strokeWidth}" fill="none" opacity="${opacity}" stroke-linecap="${lineCap}" stroke-linejoin="${lineJoin}"`

    if (dashArray.length > 0) {
      svgAttributes += ` stroke-dasharray="${dashArray.join(',')}"`
    }
    if (dashOffset !== 0) {
      svgAttributes += ` stroke-dashoffset="${dashOffset}"`
    }

    return `  <path ${svgAttributes} />\n`
  }

  const circleToSVG = (shape: KonvaCircle, layerPosition: LayerPosition, contentX: number, contentY: number): string => {
    const x = ((shape as { x?: () => number }).x?.() ?? 0) + layerPosition.x - contentX
    const y = ((shape as { y?: () => number }).y?.() ?? 0) + layerPosition.y - contentY
    const radius = (shape as { radius?: () => number }).radius?.() ?? 0
    const fill = (shape as { fill?: () => string }).fill?.() ?? '#000000'
    const opacity = (shape as { opacity?: () => number }).opacity?.() ?? 1

    if (isFinite(x) && isFinite(y) && isFinite(radius) && radius > 0) {
      return `  <circle cx="${x}" cy="${y}" r="${radius}" fill="${fill}" opacity="${opacity}" />\n`
    }
    return ''
  }

  const processGroupForSVG = (group: KonvaGroup, layerPosition: LayerPosition, contentX: number, contentY: number, svgContent: string[]): void => {
    const children = (group as { children?: KonvaShape[] }).children
    if (!children) return

    children.forEach((grandChild: KonvaShape) => {
      if (isOriginalPath(grandChild)) return

      if (grandChild.className === 'Line' || grandChild.className === 'Path') {
        const path = lineToSVGPath(grandChild as KonvaLine, layerPosition, contentX, contentY)
        if (path) svgContent.push(path)
      } else if (grandChild.className === 'Group') {
        processGroupForSVG(grandChild as KonvaGroup, layerPosition, contentX, contentY, svgContent)
      } else if (grandChild.className === 'Circle') {
        const circle = circleToSVG(grandChild as KonvaCircle, layerPosition, contentX, contentY)
        if (circle) svgContent.push(circle)
      }
    })
  }

  const exportCanvasAsSVG = (): void => {
    try {
      if (!canvasStore.stage) {
        console.error('Canvas stage not available for export')
        return
      }

      const stage = canvasStore.stage
      const contentBounds = calculateContentBounds(100)
      const { x: contentX, y: contentY, width: contentWidth, height: contentHeight } = contentBounds

      const svgParts: string[] = [
        `<svg xmlns="http://www.w3.org/2000/svg" width="${contentWidth}" height="${contentHeight}" viewBox="0 0 ${contentWidth} ${contentHeight}">\n`
      ]

      const konvaLayers = stage.getLayers()

      konvaLayers.forEach((konvaLayer: KonvaLayer) => {
        if (konvaLayer.visible() && konvaLayer.name() !== 'background') {
          const layerPosition = getLayerPosition(konvaLayer)
          const children = (konvaLayer as { children?: KonvaShape[] }).children

          if (children) {
            children.forEach((child: KonvaShape) => {
              if (isOriginalPath(child)) return

              if (child.className === 'Line' || child.className === 'Path') {
                const path = lineToSVGPath(child as KonvaLine, layerPosition, contentX, contentY)
                if (path) svgParts.push(path)
              } else if (child.className === 'Group' || (child as { children?: KonvaShape[] }).children) {
                processGroupForSVG(child as KonvaGroup, layerPosition, contentX, contentY, svgParts)
              } else if (child.className === 'Circle') {
                const circle = circleToSVG(child as KonvaCircle, layerPosition, contentX, contentY)
                if (circle) svgParts.push(circle)
              }
            })
          }
        }
      })

      svgParts.push('</svg>')
      const svgContent = svgParts.join('')

      const blob = new Blob([svgContent], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)
      const filename = generateFilename('svg')

      downloadFile(url, filename)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export SVG:', error)
    }
  }

  return {
    exportCanvasAsPNG,
    exportCanvasAsSVG
  }
}